import React, { useState, useEffect, useContext, useMemo } from "react";
import {
  Modal,
  Button,
  Row,
  Col,
  Card,
  Form,
  Accordion,
  Collapse,
  OverlayTrigger,
  Tooltip,
} from "react-bootstrap";
import { getPlanAppsHelper, buyPlanAppsHelper } from "./PlansHelper"; // your helper functions
import { toWordCase } from "../../../../Utils/utils"; // your utility function
import {
  countDaysFromNow,
  getAppPrice,
  getRenewalDates,
  getTotalCharge,
} from "./helpers"; // updated import
import { ToastContext } from "../../../../Context/ToastContext";
import { AuthContext } from "../../../../Context/AuthContext";
import { Link } from "react-router-dom";
import { MyWalletContext } from "../../../../Context/MyWalletContext";
// import { TabContext } from "./TabContext";

const preSelectApps = {
  pipeline: ["pipeline_deals"],
  deal_import_api: ["pipeline_deals"],
  custom_sales_pipeline: ["pipeline_deals"],
  ab_testing: ["ab_testing"],
  forms: ["forms"],
  advance_form_builder: ["forms"],
  landing_page: ["landing_pages"],
  advance_page_builder: ["landing_pages"],
  email: ["email_marketing"],
  email_marketing: ["email_marketing"],
  agreement: ["agreements_with_e_sign"],
  agreement_esign: ["agreements_with_e_sign"],
  agreement_template: ["agreements_with_e_sign"],
  appointment: ["appointments"],
  booking_form: ["booking"],
  contact_import_api: ["sales_crm"],
  coupon: ["coupons"],
  custom_role_permission: ["sales_crm"],
  invoicing: ["invoicing"],
  estimate_and_quote: ["invoicing", "estimates_quotes", "estimate_plus"],
  plan_and_subscription: ["plans_subscriptions", "invoicing"],
  project_management: ["invoicing", "estimates_quotes", "estimate_plus"],
  service_and_product: ["services_products"],
  sms_marketing: ["sms_marketing"],
  whatsapp_marketing: ["whatsapp_marketing"],
  whatsapp: ["whatsapp_marketing"],
  online_payment: ["online_payments"],
  ads_management: ["ads_management"],
  dedicated_resources: ["dedicated_resources"],
  one_on_one_onboarding: ["dedicated_resource"],
  api_access: ["api_access"],
  ai_chatbot: ["ai_chatbot"],
  in_app_calling: ["in_app_calling"],
  smm: ["social_media_marketing"],
  white_label: ["white_label"],
};

const BuyMoreAppsModal = ({
  show,
  onHide,
  boughtApps,
  teamId,
  pName = null,
  paymentMethodListState = null,
  handleShowPaymentMethod = () => {},
  setActiveTab = () => {},
}) => {
  const { userData } = useContext(AuthContext);
  const { addToast } = useContext(ToastContext);
  const { walletAmount, fetchWallet } = useContext(MyWalletContext);
  const [subscribedPlan, setSubscribedPlan] = useState(null);
  // state to toggle Purchased Apps section
  const [showPurchased, setShowPurchased] = useState(false);
  // availableApps are fetched when the modal is shown
  const [availableApps, setAvailableApps] = useState([]);
  const [appsLoaded, setAppsLoaded] = useState(false);
  // selectedApps will include preselected apps (bought ones if available) and newly selected apps
  const [selectedApps, setSelectedApps] = useState({});
  const [consent, setConsent] = useState(false);
  const [loading, setLoading] = useState(false);

  const billingCycle = subscribedPlan?.billing_cycle || "monthly";
  const isCustomPlan = subscribedPlan?.plan?.is_custom;
  const isOnTrial = !!subscribedPlan?.trial_expiry_date;
  // Get next billing date from subscribedPlan if available
  const nextBillingDate =
    subscribedPlan && subscribedPlan.next_payment_date
      ? new Date(subscribedPlan.next_payment_date)
      : null;
  const formattedNextBillingDate = nextBillingDate
    ? nextBillingDate.toLocaleDateString()
    : "N/A";

  useEffect(() => {
    if (userData?.authData?.subscription) {
      const { name, id } = userData?.authData?.subscription?.plan || {
        name: null,
        id: null,
      };
      setSubscribedPlan({
        ...(userData?.authData?.subscription || {}),
        plan_name: name || "Unknown",
        plan_id: id,
      });
    }
  }, [userData]);

  // Fetch available apps when modal opens
  useEffect(() => {
    if (!show) {
      setAppsLoaded(false);
      return;
    }

    if (!isCustomPlan || appsLoaded) {
      return;
    }

    setAppsLoaded(true);
    getPlanAppsHelper({ team_id: teamId })
      .then((data) => {
        fetchWallet();
        const apps = data.data || data;
        setAvailableApps(Array.isArray(apps) ? apps : []);
        // Preselect apps that are already bought (if they exist in availableApps)
        const boughtIds = new Set(boughtApps.map((app) => app.id));
        const preselected = {};
        (Array.isArray(apps) ? apps : []).forEach((app) => {
          if (boughtIds.has(app.id)) {
            preselected[app.id] = app;
          } else if (
            pName &&
            Array.isArray(preSelectApps[pName]) &&
            preSelectApps[pName].some((item) => item === app.name)
          ) {
            preSelectApps[pName].forEach((item) => {
              if (item === app.name) {
                preselected[app.id] = app;
              }
            });
          }
        });
        setSelectedApps(preselected);
      })
      .catch(() => {
        addToast(
          {
            id: "error",
            message: "Error fetching available apps.",
            title: "Error!",
          },
          "danger",
          5000
        );
      });
  }, [show, isCustomPlan, teamId, boughtApps, addToast, fetchWallet, pName]);

  // Toggle app selection (only if the app is not already bought)
  const toggleAppSelection = (app) => {
    const isBought = !!boughtApps.find((b) => b.id === app.id);
    if (isBought) return;
    setSelectedApps((prev) => {
      let newSelection = { ...prev };
      if (newSelection[app.id]) {
        delete newSelection[app.id];
      } else {
        const selectedAppData = addAppWithDependencies(app, selectedApps);
        let updatedAppData = {};
        selectedAppData.forEach((item) => {
          updatedAppData = { ...updatedAppData, [item.id]: { ...item } };
        });
        // newSelection[app.id] = app;
        newSelection = {
          ...newSelection,
          ...updatedAppData,
        };
      }
      return newSelection;
    });
  };

  // Helper: Recursively add an app and its dependencies.
  const addAppWithDependencies = (app, currentSelection) => {
    const newSelection = new Map();
    currentSelection?.length > 0 &&
      currentSelection.forEach((a) => newSelection.set(a.id, a));
    const visited = new Set();

    const addRecursively = (app) => {
      if (visited.has(app.id)) return;
      visited.add(app.id);
      newSelection.set(app.id, app);
      try {
        const deps = JSON.parse(app.dependent_on);
        if (deps) {
          if (deps.all && Array.isArray(deps.all)) {
            deps.all.forEach((depName) => {
              const depApp = availableApps.find((a) => a.name === depName);
              if (depApp) {
                addRecursively(depApp);
              }
            });
          }
          if (deps.atleast_one && Array.isArray(deps.atleast_one)) {
            deps.atleast_one.forEach((depName) => {
              const depApp = availableApps.find((a) => a.name === depName);
              if (depApp) {
                addRecursively(depApp);
              }
            });
          }
        }
      } catch (e) {
        // Ignore parsing errors.
      }
    };
    addRecursively(app);
    return Array.from(newSelection.values());
  };

  // // Toggle app selection, preventing selection for fixed or upcoming apps.
  // const handlePlanAppToggle = (app) => {
  //   if (app.upcoming === 1) return;
  //   if (app.name === "sales_crm") return;
  //   const exists = selectedPlanApps.find((a) => a.id === app.id);
  //   if (exists) {
  //     // Remove app from selection.
  //     setSelectedPlanApps((prev) => prev.filter((a) => a.id !== app.id));
  //   } else {
  //     // Add app with its dependencies.
  //     const newSelection = addAppWithDependencies(app, selectedPlanApps);
  //     setSelectedPlanApps(newSelection);
  //   }
  // };

  // Remove an app from the selection (only applicable for new selections)
  const onRemoveApp = (app) => {
    setSelectedApps((prev) => {
      const newSelection = { ...prev };
      delete newSelection[app.id];
      return newSelection;
    });
  };

  // Group available apps by category for the left column
  const groupedAvailableApps = (availableApps || []).reduce((groups, app) => {
    const category = app.category || "Others";
    if (!groups[category]) groups[category] = [];
    groups[category].push(app);
    return groups;
  }, {});

  const currentCycleDays = useMemo(() => {
    const { currentCycleStart, nextRenewalDate } = getRenewalDates(
      subscribedPlan?.billing_cycle || "monthly",
      subscribedPlan?.start_at || ""
    );
    return countDaysFromNow(nextRenewalDate, currentCycleStart);
    // return (nextRenewalDate - currentCycleStart) / (1000 * 60 * 60 * 24);
  }, [subscribedPlan]);
  // For dynamic prorated calculation, assume a cycle of 30 days.
  const daysRemaining = useMemo(() => {
    if (boughtApps.length > 0 && boughtApps[0].next_payment_date) {
      const diffDays = countDaysFromNow(
        boughtApps[0].next_payment_date,
        null,
        false // Remove this if user want to include current day in billing.
      );
      return diffDays;
    }
    return currentCycleDays; // default value if no bought app with a next payment date
  }, [selectedApps]);
  // Compute totals:
  // newAppsTotal: only include apps that are newly selected (i.e. not already bought)
  const newAppsTotal = Object.values(selectedApps).reduce((acc, app) => {
    const priceObj = JSON.parse(app.price);
    let price = parseFloat(priceObj[billingCycle] || 0);
    let oneDayPrice = getTotalCharge(billingCycle, price) / currentCycleDays;
    return (
      acc +
      (boughtApps.find((b) => b.id === app.id)
        ? 0
        : oneDayPrice * daysRemaining)
    );
  }, 0);

  // (price / currentCycleDays) * daysRemaining)

  // boughtTotal: amount of apps that are already purchased (using the "amount" field if available)
  /*const boughtTotal = boughtApps.reduce((acc, app) => {
    const price = app.amount ? parseFloat(app.amount) : 0;
    const remainingDaysPrice = (price / 30) * daysRemaining;
    return acc + parseFloat(remainingDaysPrice.toFixed(2));
  }, 0);
  */

  const boughtTotal = Object.values(selectedApps).reduce((acc, app) => {
    return (
      acc +
      (boughtApps.find((b) => b.id === app.id)
        ? getAppPrice(app, billingCycle)
        : 0)
    );
  }, 0);

  // overallTotal: sum of both
  // const overallTotal = boughtTotal + newAppsTotal;
  const overallTotal = Object.values(selectedApps).reduce((acc, app) => {
    return acc + getAppPrice(app, billingCycle);
  }, 0);
  // const proratedCalculated = ((overallTotal / 30) * daysRemaining).toFixed(2);
  const calculatedPayableValue =
    walletAmount > newAppsTotal ? 0 : newAppsTotal - walletAmount;

  const proratedCalculated = parseFloat(calculatedPayableValue.toFixed(2));
  const amountAdjustedFromWallet =
    walletAmount > newAppsTotal ? newAppsTotal : walletAmount;
  // Prepare sorted array for right column: already bought apps come first, new apps at last.
  const sortedSelectedApps = Object.values(selectedApps).sort((a, b) => {
    const aBought = !!boughtApps.find((x) => x.id === a.id);
    const bBought = !!boughtApps.find((x) => x.id === b.id);
    if (aBought && !bBought) return -1;
    if (!aBought && bBought) return 1;
    return 0;
  });

  // Handle purchase button click
  const handleBuyApps = () => {
    if (!consent) {
      addToast(
        {
          id: "error",
          message:
            "You have to give consent that you will be charged per month onwards.",
          title: "Consent Required!",
        },
        "danger",
        5000
      );
      return;
    }
    const shouldCheckPaymentMethods = Array.isArray(paymentMethodListState);
    if (shouldCheckPaymentMethods && paymentMethodListState.length === 0) {
      onHide();
      handleShowPaymentMethod();
      return;
    }

    // Only purchase new apps (exclude already bought apps)
    const selectedIds = Object.keys(selectedApps)
      .map((id) => parseInt(id, 10))
      .filter((id) => !boughtApps.find((b) => b.id === id));
    if (selectedIds.length === 0) {
      addToast(
        {
          id: "error",
          message: "No new apps selected to purchase.",
          title: "Selection Required!",
        },
        "danger",
        5000
      );
      return;
    }
    setLoading(true);
    buyPlanAppsHelper({ team_id: teamId, id: selectedIds })
      .then(() => {
        fetchWallet();
        addToast(
          {
            id: "success",
            message: "Apps purchased successfully.",
            title: "Success!",
          },
          "success",
          5000
        );
        setLoading(false);
        onHide();
      })
      .catch((error) => {
        console.log("error:", error);
        addToast(
          {
            id: "error",
            message: "Error purchasing apps.",
            title: "Error!",
          },
          "danger",
          5000
        );
        setLoading(false);
      });
  };

  return (
    <Modal
      show={show}
      onHide={onHide}
      size="xl"
      centered
      className="common modalXl"
    >
      <Modal.Header
        closeButton
        className="modal-head"
        closeVariant="white"
      >
        <Modal.Title className="text-dark secondary-font text-uppercase">
          Buy More APPS
        </Modal.Title>
      </Modal.Header>
      {isCustomPlan ? (
        <Modal.Body>
          <Row>
            {/* Left Column */}
            <Col md={8}>
              {/* Collapsible Purchased Apps Section */}
              {boughtApps.length > 0 && (
                <div className="purchased-apps-section mb-4">
                  <div
                    className="purchased-apps-header d-flex justify-content-between align-items-center"
                    onClick={() => setShowPurchased(!showPurchased)}
                  >
                    <h5 className="mb-0 ms-3">Purchased Apps</h5>
                    <i
                      className={`${showPurchased ? "ri-subtract-line" : "ri-add-line"} me-3 fs-5`}
                    ></i>
                  </div>
                  <Collapse in={showPurchased}>
                    <div>
                      <hr style={{ borderColor: "rgb(204, 204, 204)" }} />
                      <div
                        style={{
                          display: "grid",
                          gridTemplateColumns: "repeat(2, 1fr)",
                          gap: "1rem",
                        }}
                      >
                        {boughtApps.map((app) => {
                          // Fetch icon from availableApps if available
                          const relatedApp = availableApps.find(
                            (a) => a.id === app.id
                          );
                          const icon = relatedApp
                            ? relatedApp.icon
                            : app.icon || "ri-settings-line";
                          return (
                            <div
                              key={app.id}
                              className="app-item disabled-app"
                              style={{
                                position: "relative",
                                textAlign: "center",
                                cursor: "not-allowed",
                                border: "1px solid #ddd",
                                padding: "1rem",
                                borderRadius: "6px",
                              }}
                            >
                              <i
                                className={icon}
                                style={{ fontSize: "24px" }}
                              ></i>
                              <div>{toWordCase(app.name)}</div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  </Collapse>
                </div>
              )}
              <hr style={{ borderColor: "rgb(204, 204, 204)" }} />
              <h5 className="mb-0 ms-3">Choose Plan Apps To Buy</h5>
              <hr style={{ borderColor: "rgb(204, 204, 204)" }} />
              <div className="section-container mb-4 p-3 border rounded">
                {Object.keys(groupedAvailableApps).map((category) => (
                  <div
                    key={category}
                    className="mb-4"
                  >
                    <h5>{toWordCase(category)}</h5>
                    <hr style={{ borderColor: "rgb(204, 204, 204)" }} />
                    <div
                      style={{
                        display: "grid",
                        gridTemplateColumns: "repeat(2, 1fr)",
                        gap: "1rem",
                      }}
                    >
                      {groupedAvailableApps[category].map((app) => {
                        const isBought = !!boughtApps.find(
                          (b) => b.id === app.id
                        );
                        const isSelected = !!selectedApps[app.id];
                        return (
                          <div
                            key={app.id}
                            className={`app-item border p-2 m-1 ${isSelected ? "selected" : ""} ${isBought ? "disabled-app" : ""}`}
                            style={{
                              cursor: isBought ? "not-allowed" : "pointer",
                              textAlign: "center",
                              position: "relative",
                              borderRadius: "6px",
                            }}
                            onClick={() => {
                              if (app.name !== "ads_management") {
                                if (!isBought) toggleAppSelection(app);
                              }
                            }}
                          >
                            <div className="d-flex align-items-center justify-content-center pb-3">
                              <i
                                className={app.icon || "ri-settings-line"}
                                style={{ fontSize: "24px" }}
                              ></i>
                              <span className="ms-1">
                                {toWordCase(app.name)}
                              </span>
                              <OverlayTrigger
                                placement="top"
                                overlay={
                                  <Tooltip id={`tooltip-${app.id}`}>
                                    {app.description}
                                  </Tooltip>
                                }
                              >
                                <i
                                  className="ri-question-line ms-auto"
                                  style={{ cursor: "help", zIndex: 2 }}
                                ></i>
                              </OverlayTrigger>
                            </div>
                            <div
                              style={{
                                position: "absolute",
                                bottom: "8px",
                                right: "8px",
                                fontSize: "12px",
                                textAlign: "right",
                              }}
                            >
                              {app.name !== "ads_management" ? (
                                isBought ? (
                                  <span
                                    style={{
                                      color: "green",
                                      fontWeight: "bold",
                                    }}
                                  >
                                    Purchased
                                  </span>
                                ) : isSelected ? (
                                  <i
                                    className="ri-checkbox-fill"
                                    style={{ color: "green", fontSize: "14px" }}
                                  ></i>
                                ) : (
                                  <i
                                    className="ri-checkbox-blank-line"
                                    style={{ color: "gray", fontSize: "14px" }}
                                  ></i>
                                )
                              ) : (
                                <span
                                  style={{ color: "green", fontWeight: "bold" }}
                                >
                                  Coming Soon
                                </span>
                              )}
                            </div>
                            {isBought && (
                              <div
                                style={{
                                  position: "absolute",
                                  top: 0,
                                  left: 0,
                                  right: 0,
                                  bottom: 0,
                                  background:
                                    "repeating-linear-gradient(45deg, rgba(0,0,0,0.03), rgba(0,0,0,0.03) 2px, transparent 2px, transparent 4px)",
                                  pointerEvents: "none",
                                  borderRadius: "6px",
                                }}
                              ></div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
              <div className="mt-3">
                <div className="section-container mb-4 p-3 border rounded">
                  <h5>FAQs</h5>
                  <hr style={{ borderColor: "#ccc" }} />
                  <Accordion defaultActiveKey="0">
                    <Accordion.Item eventKey="0">
                      <Accordion.Header>
                        Prorated Pricing Details
                      </Accordion.Header>
                      <Accordion.Body>
                        <p>
                          Prices are calculated based on your current billing
                          cycle (<strong>{billingCycle}</strong>).
                        </p>
                        <p style={{ fontStyle: "italic" }}>
                          {/* Prorated Price = ${proratedCalculated} */}
                          Prorated Price = ((Total Amount - Paid Amount) /
                          {
                            currentCycleDays
                          }) * {daysRemaining} = ${proratedCalculated}
                        </p>
                        {nextBillingDate ? (
                          <p>
                            With {daysRemaining} days remaining until your next
                            billing date on{" "}
                            <strong>{formattedNextBillingDate}</strong>, your
                            prorated charge is{" "}
                            <strong>${proratedCalculated}</strong>. On your next
                            billing cycle, your custom plan will be charged the
                            full amount of{" "}
                            <strong>
                              ${overallTotal ? overallTotal.toFixed(2) : 0}
                            </strong>
                            .
                          </p>
                        ) : (
                          <p>
                            Your prorated charge is{" "}
                            <strong>${proratedCalculated}</strong>. On your next
                            billing cycle, you will be charged the full amount
                            of{" "}
                            <strong>
                              {overallTotal ? overallTotal.toFixed(2) : 0}
                            </strong>{" "}
                            for this custom plan.
                          </p>
                        )}
                      </Accordion.Body>
                    </Accordion.Item>
                  </Accordion>
                </div>
              </div>
            </Col>
            {/* Right Column */}
            <Col md={4}>
              <Card className="shadow-sm">
                <Card.Header className="bg-primary text-white text-center p-2">
                  Subscription & Purchase Summary
                </Card.Header>
                <Card.Body className="p-0 px-2 pt-2">
                  {sortedSelectedApps.length > 0 ? (
                    sortedSelectedApps.map((app) => {
                      const isBought = !!boughtApps.find(
                        (b) => b.id === app.id
                      );
                      return (
                        <div
                          key={app.id}
                          className="selected-app-box"
                          style={{
                            border: "1px solid #ddd",
                            borderRadius: "6px",
                            marginBottom: "0.5rem",
                            padding: "0.5rem",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            position: "relative",
                          }}
                        >
                          <div
                            className="selected-app-info"
                            style={{ display: "flex", alignItems: "center" }}
                          >
                            <i
                              className={app.icon}
                              style={{ fontSize: "24px", marginRight: "8px" }}
                            ></i>
                            <span>{toWordCase(app.name)}</span>
                          </div>
                          <div
                            className="selected-app-details"
                            style={{ display: "flex", alignItems: "center" }}
                          >
                            <span style={{ marginRight: "8px" }}>
                              ${getAppPrice(app, billingCycle).toFixed(2)}
                            </span>
                            {!isBought && (
                              <Button
                                variant="link"
                                size="sm"
                                onClick={() => onRemoveApp(app)}
                                style={{ border: "none", padding: 0 }}
                              >
                                <i
                                  className="ri-close-line"
                                  style={{ fontSize: "1.2rem", color: "red" }}
                                ></i>
                              </Button>
                            )}
                          </div>
                          {isBought && (
                            <div
                              style={{
                                position: "absolute",
                                top: 0,
                                left: 0,
                                right: 0,
                                bottom: 0,
                                background:
                                  "repeating-linear-gradient(45deg, rgba(0,0,0,0.03), rgba(0,0,0,0.03) 2px, transparent 2px, transparent 4px)",
                                pointerEvents: "none",
                                borderRadius: "6px",
                              }}
                            ></div>
                          )}
                        </div>
                      );
                    })
                  ) : (
                    <p className="text-center">No apps selected</p>
                  )}
                </Card.Body>
                <Card.Footer>
                  <div className="summary-details">
                    {isOnTrial ? (
                      <>
                        <div className="d-flex justify-content-between mb-1">
                          <strong>Total Amount to Pay After Trial:</strong>
                          <span>
                            ${overallTotal ? overallTotal.toFixed(2) : 0}
                          </span>
                        </div>
                        <p className="text-muted small mb-0">
                          You will be charged this amount when your trial ends
                          {nextBillingDate
                            ? ` on ${formattedNextBillingDate}`
                            : ""}
                          .
                        </p>
                      </>
                    ) : (
                      <>
                        <div className="d-flex justify-content-between mb-1">
                          <strong>Total Amount:</strong>
                          <span>
                            ${overallTotal ? overallTotal.toFixed(2) : 0}
                          </span>
                        </div>
                        <div className="d-flex justify-content-between mb-1">
                          <strong>Paid Amount:</strong>
                          <span>${boughtTotal.toFixed(2)}</span>
                        </div>
                        <div className="d-flex justify-content-between">
                          <strong>Wallet Amount:</strong>
                          <span>${walletAmount.toFixed(2)}</span>
                        </div>
                        <div className="d-flex justify-content-between">
                          <strong>Adjusted Amount From Wallet:</strong>
                          <span>${amountAdjustedFromWallet.toFixed(2)}</span>
                        </div>
                        <div className="d-flex justify-content-between">
                          <strong>Final Amount to Pay:</strong>
                          <span>${proratedCalculated}</span>
                        </div>
                      </>
                    )}
                  </div>
                  <hr style={{ borderColor: "rgb(204, 204, 204)" }} />
                  <Form.Group className="mt-2">
                    <Form.Check
                      type="checkbox"
                      // label={`I confirm that I will be charged $${overallTotal.toFixed(2)} per ${billingCycle.replace(/ly$/, "")} from the next billing cycle.`}
                      label={
                        isOnTrial
                          ? `I consent to be charged a ${billingCycle} fee of $${overallTotal ? overallTotal.toFixed(2) : 0}  starting from the next billing period.`
                          : newAppsTotal === 0
                            ? `I consent that to being charged $${overallTotal ? overallTotal.toFixed(2) : 0} per ${billingCycle.replace(/ly$/, "")} from the next billing cycle.`
                            : `I consent to being charged $${proratedCalculated} now, followed by a $${overallTotal ? overallTotal.toFixed(2) : 0} ${billingCycle.replace(/ly$/, "")} subscription fee from the next billing period onward.`
                      }
                      checked={consent}
                      onChange={(e) => setConsent(e.target.checked)}
                    />
                  </Form.Group>
                </Card.Footer>
              </Card>
            </Col>
          </Row>
        </Modal.Body>
      ) : (
        <Modal.Body
          className="text-center"
          style={{ height: "300px" }}
        >
          <h4 className="mt-4">
            You are not on a custom plan to buy individual apps.
          </h4>
          <div className="my-4">
            <Link
              className="btn btn-primary btn-primary btn-sm"
              to="/settings/accounts-and-billing"
              onClick={() => {
                setActiveTab("plan");
                onHide();
              }}
            >
              Upgrade Plan
            </Link>
          </div>
        </Modal.Body>
      )}
      {isCustomPlan && (
        <Modal.Footer>
          <Button
            variant="primary"
            onClick={onHide}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleBuyApps}
            disabled={!consent || loading}
          >
            {loading ? "Processing..." : "Buy Apps"}
          </Button>
        </Modal.Footer>
      )}
    </Modal>
  );
};

export default BuyMoreAppsModal;
